import { useEffect, useState } from 'react';
import { getRate, type Rate } from './api';

/** What the rate preview knows: the rate the server would apply, or why there is none. */
export type RatePreviewState =
  | { readonly status: 'waiting' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly rate: Rate }
  | { readonly status: 'off' }
  | { readonly status: 'no-rate' }
  | { readonly status: 'failed' };

/**
 * `GET …/mileage/rate` for the trip's date and vehicle, again whenever either changes (the
 * server's rate is the one valid on the date; the trip freezes it at capture, the preview is
 * informational). Waits for a full date.
 */
export function useRatePreview(date: string, vehicleId: string): RatePreviewState {
  const [state, setState] = useState<RatePreviewState>({ status: 'waiting' });
  useEffect(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setState({ status: 'waiting' });
      return;
    }
    let live = true;
    setState({ status: 'loading' });
    void getRate(date, vehicleId === '' ? undefined : vehicleId).then((outcome) => {
      if (live) setState(stateOf(outcome));
    });
    return () => {
      live = false;
    };
  }, [date, vehicleId]);
  return state;
}

function stateOf(outcome: Awaited<ReturnType<typeof getRate>>): RatePreviewState {
  if (outcome.ok) return { status: 'ready', rate: outcome.value };
  if (outcome.code === 'MILEAGE_OFF') return { status: 'off' };
  if (outcome.code === 'NO_RATE') return { status: 'no-rate' };
  return { status: 'failed' };
}
